import { apiFetch } from "./api";
import type { RequestType } from "./employee-api";

/**
 * Tenant-level policy for how many check-in/out pairs an employee can log per
 * day. Mirror of the backend Prisma enum.
 */
export type AttendanceMode =
  | "SINGLE_SESSION_PER_DAY"
  | "MULTIPLE_SESSIONS_PER_DAY";

export interface TenantSettings {
  organizationName: string | null;
  defaultLanguage: string;
  timezone: string;
  dateFormat: string;
  gracePeriodMins: number;
  lateThresholdMins: number;
  minWorkHoursPerDay: number;
  requireApprovalForCorrections: boolean;
  autoFlagExceptions: boolean;
  defaultWorkStartTime: string;
  /** ISO weekdays the organisation operates (1 = Mon … 7 = Sun). */
  workingDays: number[];
  defaultWorkEndTime: string;
  attendanceMode: AttendanceMode;
  geofenceEnforcementEnabled: boolean;
  allowGpsOnlyFallback: boolean;
  defaultGeofenceRadiusM: number;
  /** WFH master switch + default home-geofence radius (FEAT-2026-002). */
  wfhEnabled: boolean;
  defaultHomeRadiusM: number;
  /** Web check-in switch + accuracy floor (FEAT-2026-013, BR-151/152). */
  webCheckInEnabled: boolean;
  maxCheckInAccuracyM: number;
  /** Work-From-Anywhere allowances (FEAT-2026-014, BR-161). */
  wfaEnabled: boolean;
  emailDigestEnabled: boolean;
  inAppNotificationsEnabled: boolean;
  mobilePushEnabled: boolean;
  aiPersonasConfig: AiPersonasConfig | null;
  /** Line managers decide their direct reports' requests (BR-211, FEAT-2026-027). */
  lineManagerApprovalEnabled: boolean;
  /** Stored attachment rules (BR-210), in the order the mobile app offers types. */
  requestAttachmentRules: AttachmentRule[];
  /**
   * Every offerable request type with the tenant's options (FEAT-2026-031).
   * Absent from an API that predates it; Settings → Requests then edits
   * attachment rules only.
   */
  requestTypes?: RequestTypeView[];
  updatedAt: string;
}

/**
 * One request type on Settings → Requests (FEAT-2026-031). `offered` is the
 * tenant's own switch; Work-from-anywhere also needs `wfaEnabled` to reach staff.
 */
export interface RequestTypeView {
  type: RequestType;
  offered: boolean;
  label: string | null;
  description: string | null;
  dateMode: "single" | "range";
  /** Only Other may change its dates. */
  dateModeConfigurable: boolean;
  attachment: { required: boolean; instruction: string | null };
  defaultName: string;
}

export interface AttachmentRule {
  type: RequestType;
  required: boolean;
  instruction: string | null;
}

/** `PATCH /settings/requests`: a sent `attachmentRules` replaces every rule. */
export interface UpdateRequestSettingsBody {
  lineManagerApprovalEnabled?: boolean;
  attachmentRules?: Array<{ type: RequestType; required: boolean; instruction?: string | null }>;
  /** Per-type options (FEAT-2026-031). Omitted = unchanged. `dateMode` only for Other. */
  types?: Array<{
    key: RequestType;
    offered?: boolean;
    label?: string | null;
    description?: string | null;
    dateMode?: "single" | "range";
  }>;
}

export interface AiPersonasConfig {
  enableForHR?: boolean;
  enableForSupervisors?: boolean;
  enableForMobile?: boolean;
  defaultResponseStyle?: "concise" | "detailed";
}

export type UpdateTenantSettingsBody = Partial<
  Omit<
    TenantSettings,
    "updatedAt" | "aiPersonasConfig" | "lineManagerApprovalEnabled" | "requestAttachmentRules" | "requestTypes"
  >
> & {
  aiPersonasConfig?: AiPersonasConfig;
};

/** What Settings → Requests reads and saves (FEAT-2026-027, FEAT-2026-031). */
export type RequestSettingsSlice = Pick<
  TenantSettings,
  "lineManagerApprovalEnabled" | "requestAttachmentRules" | "requestTypes" | "wfaEnabled"
>;

export const requestSlice = (s: TenantSettings): RequestSettingsSlice => ({
  lineManagerApprovalEnabled: s.lineManagerApprovalEnabled,
  requestAttachmentRules: s.requestAttachmentRules,
  requestTypes: s.requestTypes,
  wfaEnabled: s.wfaEnabled,
});

export const settingsApi = {
  get: () => apiFetch<TenantSettings>("/settings"),

  update: (body: UpdateTenantSettingsBody) =>
    apiFetch<TenantSettings>("/settings", { method: "PATCH", body }),

  /** Request settings (FEAT-2026-027): its own route, which HR Manager may call. */
  updateRequests: (body: UpdateRequestSettingsBody) =>
    apiFetch<TenantSettings>("/settings/requests", { method: "PATCH", body }),
};
